import React, { useState, useEffect } from 'react';
import { Loader2, ChevronDown, ChevronRight, Users, ShoppingBag, Euro, Mail, Phone, FileSpreadsheet, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { adminAPI, exportAPI } from '../services/api';
import CustomerAccountTools, { ProBadge } from './CustomerAccountTools';
import AdminCustomerCards from './AdminCustomerCards';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

const STATUS_COLORS = {
  'Livré': 'bg-green-100 text-green-700',
  'Expédié': 'bg-blue-100 text-blue-700',
  'En cours': 'bg-yellow-100 text-yellow-700',
};

const AdminCustomersPanel = () => {
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState(() => new URLSearchParams(window.location.search).get('q') || '');
  const [exporting, setExporting] = useState('');
  const doExport = async (fmt) => {
    setExporting(fmt);
    try { await exportAPI.customers(fmt); toast.success(`Export ${fmt.toUpperCase()} téléchargé`); } catch { toast.error('Export impossible'); } finally { setExporting(''); }
  };
  const [expanded, setExpanded] = useState(null);

  const load = () => adminAPI.getCustomers()
      .then(d => setCustomers(d.customers))
      .catch(console.error)
      .finally(() => setIsLoading(false));

  useEffect(() => {
    load();
  }, []);

  const pendingPro = customers.filter(c => c.pro?.status === 'pending').length;

  const visible = customers.filter(c =>
    !filter || [c.email, c.first_name, c.last_name, c.pro?.company_name].some(v => (v || '').toLowerCase().includes(filter.toLowerCase()))
  );
  const buyers = customers.filter(c => c.orders_count > 0).length;
  const revenue = customers.reduce((s, c) => s + c.total_spent, 0);

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">Clients ({customers.length}){pendingPro > 0 && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full font-medium" data-testid="customers-pending-pro">{pendingPro} demande{pendingPro > 1 ? 's' : ''} pro à valider</span>}</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={!!exporting} onClick={() => doExport('csv')} data-testid="customers-export-csv">{exporting === 'csv' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <FileSpreadsheet size={14} className="mr-1" />} Export CSV</Button>
          <Button variant="outline" size="sm" disabled={!!exporting} onClick={() => doExport('pdf')} data-testid="customers-export-pdf">{exporting === 'pdf' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <FileText size={14} className="mr-1" />} Export PDF</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {[
          { icon: Users, label: 'Comptes clients', value: customers.length, testid: 'customers-stat-total' },
          { icon: ShoppingBag, label: 'Clients acheteurs', value: buyers, testid: 'customers-stat-buyers' },
          { icon: Euro, label: 'Chiffre d\'affaires', value: `${revenue.toFixed(2)} €`, testid: 'customers-stat-revenue' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-lg shadow-sm p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-orange-50 text-orange-500 flex items-center justify-center"><s.icon size={20} /></div>
            <div>
              <p className="text-xs text-gray-500">{s.label}</p>
              <p className="text-lg font-bold text-gray-900" data-testid={s.testid}>{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="md:hidden space-y-3">
        <div className="bg-white rounded-lg shadow-sm p-3">
          <Input data-testid="customers-filter-mobile" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Rechercher un client (nom, email)..." />
        </div>
        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-orange-500" /></div>
        ) : (
          <AdminCustomerCards customers={visible} expanded={expanded} onToggle={(id) => setExpanded(expanded === id ? null : id)} onChanged={load} />
        )}
        {!isLoading && visible.length === 0 && <p className="text-center py-8 text-gray-500 bg-white rounded-lg shadow-sm">Aucun client trouvé</p>}
      </div>

      <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-x-auto">
        <div className="p-4 border-b">
          <Input data-testid="customers-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Rechercher un client (nom, email)..." className="max-w-sm" />
        </div>
        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-orange-500" /></div>
        ) : (
          <table className="w-full min-w-[720px]" data-testid="customers-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 w-8" />
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inscrit le</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Commandes</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total dépensé</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dernière commande</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {visible.map(c => (
                <React.Fragment key={c.id}>
                  <tr
                    data-testid={`customer-row-${c.email}`}
                    onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                    className={`cursor-pointer hover:bg-gray-50 ${expanded === c.id ? 'bg-orange-50/40' : ''}`}
                  >
                    <td className="px-4 py-3 text-gray-400">{expanded === c.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#1e3a5f] text-white flex items-center justify-center text-sm font-semibold">
                          {(c.first_name || c.email).charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{c.first_name} {c.last_name}</p>
                          {c.is_admin && <span className="text-[10px] uppercase tracking-wide bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">Admin</span>}
                          <ProBadge pro={c.pro} />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <p className="flex items-center gap-1"><Mail size={12} /> {c.email}</p>
                      {c.phone && <p className="flex items-center gap-1 text-xs text-gray-400"><Phone size={12} /> {c.phone}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{fmtDate(c.created_at)}</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-gray-900" data-testid={`customer-orders-${c.email}`}>{c.orders_count}</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-gray-900" data-testid={`customer-spent-${c.email}`}>{c.total_spent.toFixed(2)} €</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{fmtDate(c.last_order_at)}</td>
                  </tr>
                  {expanded === c.id && (
                    <tr data-testid={`customer-orders-panel-${c.email}`}>
                      <td colSpan={7} className="bg-gray-50 px-8 py-4">
                        <CustomerAccountTools customer={c} onChanged={load} />
                        {c.orders.length === 0 ? (
                          <p className="text-sm text-gray-500">Aucune commande pour ce client.</p>
                        ) : (
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-xs text-gray-500 uppercase">
                                <th className="text-left py-1">N° commande</th>
                                <th className="text-left py-1">Date</th>
                                <th className="text-left py-1">Ville</th>
                                <th className="text-right py-1">Articles</th>
                                <th className="text-right py-1">Total</th>
                                <th className="text-left py-1 pl-4">Statut</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {c.orders.map(o => (
                                <tr key={o.id}>
                                  <td className="py-2 font-medium text-blue-600">{o.order_number || o.id.slice(0, 8)}</td>
                                  <td className="py-2 text-gray-600">{fmtDate(o.created_at)}</td>
                                  <td className="py-2 text-gray-600">{o.city || '—'}</td>
                                  <td className="py-2 text-right text-gray-600">{o.items_count}</td>
                                  <td className="py-2 text-right font-medium text-gray-900">{o.total.toFixed(2)} €</td>
                                  <td className="py-2 pl-4">
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[o.status] || 'bg-gray-100 text-gray-700'}`}>{o.status}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
        {!isLoading && visible.length === 0 && <p className="text-center py-8 text-gray-500">Aucun client trouvé</p>}
      </div>
    </>
  );
};

export default AdminCustomersPanel;
